import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { useBandStore } from './bandStore'
import type { BandEventSummaryDto, BandEventDto, CreateBandEventDto, UpdateBandEventDto } from '../dtos'

export const useBandEventStore = defineStore('bandEvent', () => {
    const userStore = useUserStore()
    const bandStore = useBandStore()

    const events = ref<BandEventSummaryDto[]>([])
    const loading = ref(false)
    const isFetching = ref(false)

    async function fetchBandEvents(bandId: number) {
        if (!userStore.user || isFetching.value) return
        loading.value = true
        isFetching.value = true
        events.value = []
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/band-events/band/${bandId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            events.value = response.data
        } catch (error) {
            console.error('Failed to fetch events:', error)
        } finally {
            loading.value = false
            isFetching.value = false
        }
    }

    async function getEventDetails(eventId: number): Promise<BandEventDto | null> {
        if (!userStore.user) return null
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/band-events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            return response.data
        } catch (error) {
            console.error('Failed to fetch event details:', error)
            return null
        }
    }

    async function createEvent(dto: Omit<CreateBandEventDto, 'bandId'>) {
        if (!userStore.user || !bandStore.currentBand) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: CreateBandEventDto = { ...dto, bandId: bandStore.currentBand.id }
            const response = await api.post('/band-events', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = response.data as BandEventDto
            const summary: BandEventSummaryDto = {
                id: data.id,
                name: data.name,
                eventDate: data.eventDate,
                durationMinutes: data.durationMinutes,
                location: data.location,
                bandId: data.bandId,
                memberCount: data.members?.length || 0,
                isCurrentUserAttending: data.members?.some(m => m.userId === userStore.dbUser?.id) || false,
                setlistId: data.setlistId,
                setlistName: data.setlistName,
                updatedAt: data.updatedAt,
                rowVersion: data.rowVersion
            }
            events.value.push(summary)
            events.value.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
            return data
        } catch (error) {
            console.error('Failed to create event:', error)
            throw error
        }
    }

    async function updateEvent(eventId: number, dto: UpdateBandEventDto) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.put(`/band-events/${eventId}`, dto, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = response.data as BandEventDto
            const idx = events.value.findIndex(e => e.id === eventId)
            if (idx !== -1) {
                events.value[idx] = {
                    id: data.id,
                    name: data.name,
                    eventDate: data.eventDate,
                    durationMinutes: data.durationMinutes,
                    location: data.location,
                    bandId: data.bandId,
                    memberCount: data.members?.length || 0,
                    isCurrentUserAttending: data.members?.some(m => m.userId === userStore.dbUser?.id) || false,
                    setlistId: data.setlistId,
                    setlistName: data.setlistName,
                    updatedAt: data.updatedAt,
                    rowVersion: data.rowVersion
                }
                events.value.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
            }
            return data
        } catch (error) {
            console.error('Failed to update event:', error)
            throw error
        }
    }

    async function deleteEvent(eventId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/band-events/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            events.value = events.value.filter(e => e.id !== eventId)
        } catch (error) {
            console.error('Failed to delete event:', error)
            throw error
        }
    }

    async function toggleAttendance(eventId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post(`/band-events/${eventId}/toggle-attendance`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const summary = response.data as BandEventSummaryDto
            const idx = events.value.findIndex(e => e.id === eventId)
            if (idx !== -1) {
                events.value[idx] = summary
            }
        } catch (error) {
            console.error('Failed to toggle attendance:', error)
            throw error
        }
    }

    function clear() {
        events.value = []
        loading.value = false
        isFetching.value = false
    }

    return {
        events, loading, isFetching,
        fetchBandEvents, getEventDetails, createEvent, updateEvent, deleteEvent, toggleAttendance, clear
    }
})
